import { useRouter, useLocalSearchParams, useNavigation } from "expo-router";
import { RoutineCustomization } from "@src/features/alarms/components/RoutineCustomization";
import { useUpdateAlarmRoutine, useAlarm } from "@src/services/api/hooks";
import { useMemo, useLayoutEffect, useCallback, useRef, useState } from "react";
import { track } from "@visioni/a3s-analytics";
import { AlarmRoutineEdited } from "@src/features/alarms/analytics";
import { cameraScope } from "@src/features/cameras/analytics";
import { useTenantId } from "@src/features/tenants";
import { Pressable, ActivityIndicator } from "react-native";
import { Text, View } from "@src/components";
import { getTimezoneFromAlarmDetail } from "@src/features/alarms/utils/alarm-utils";
import { showSuccessToast, showErrorToast } from "@src/utils/toast";
import { useHeaderBackButton } from "@src/hooks/useHeaderBackButton";
import { isWeb } from "@src/utils/web-helpers";
import { useUnistyles } from "react-native-unistyles";
import { RotatingAlarmRoutine, WeeklyAlarmRoutine } from "@visioni/a3s-alarm-schedules/schema";

export default function CustomizeRoutineScreen() {
  const { theme } = useUnistyles();
  const router = useRouter();
  const navigation = useNavigation();
  const { headerLeft } = useHeaderBackButton("/agent");
  const {
    siteId,
    cameraKey,
    routine: routineParam,
  } = useLocalSearchParams<{
    siteId: string;
    cameraKey: string;
    routine?: string;
  }>();
  const updateRoutineMutation = useUpdateAlarmRoutine();
  const { data: alarm } = useAlarm(siteId, cameraKey);
  const [routineData, setRoutineData] = useState<WeeklyAlarmRoutine | RotatingAlarmRoutine | null>(null);
  const tenantId = useTenantId();
  const editedRef = useRef(false);
  const handleEdit = useCallback(() => {
    if (editedRef.current) return;
    editedRef.current = true;
    track(AlarmRoutineEdited, cameraScope(tenantId, siteId, cameraKey));
  }, [tenantId, siteId, cameraKey]);

  // Parse the routine from query params if provided
  const initialRoutine = useMemo(() => {
    if (!routineParam) return undefined;
    try {
      const decoded = decodeURIComponent(routineParam);
      const parsed = JSON.parse(decoded) as WeeklyAlarmRoutine | RotatingAlarmRoutine;
      return parsed;
    } catch (error) {
      console.error("Failed to parse routine from params:", error);
      return undefined;
    }
  }, [routineParam]);

  const handleSave = useCallback(() => {
    if (!cameraKey) {
      showErrorToast({ message: "Error", description: "Camera key is required to save the routine." });
      return;
    }

    if (!routineData) {
      showErrorToast({ message: "Error", description: "Please configure a routine before saving." });
      return;
    }

    const timezone = getTimezoneFromAlarmDetail(alarm);
    if (!timezone) {
      showErrorToast({ message: "Error", description: "Schedule data is not available. Please try again." });
      return;
    }

    updateRoutineMutation.mutate(
      { siteId, cameraKey, routine: routineData, timezone },
      {
        onSuccess: () => {
          showSuccessToast({ message: "Routine saved" });
          router.back();
        },
        onError: (error) => {
          console.error("Failed to update alarm routine:", error);
          showErrorToast({ message: "Error", description: "Failed to update alarm routine. Please try again." });
        },
      },
    );
  }, [cameraKey, siteId, routineData, alarm, updateRoutineMutation, router]);

  // Configure navigation header
  useLayoutEffect(() => {
    navigation.setOptions({
      title: "Customise Routine",
      headerBackButtonDisplayMode: "minimal",
      headerStyle: {
        backgroundColor: theme.colors.background,
      },
      headerTintColor: theme.colors.text,
      headerLeft,
      headerRight: () => (
        <Pressable
          onPress={handleSave}
          style={{ paddingHorizontal: 16 }}
          disabled={updateRoutineMutation.isPending || !routineData}
        >
          {updateRoutineMutation.isPending ?
            <ActivityIndicator size="small" />
          : <Text textStyle="md/semibold" color={routineData ? "navy" : "textMuted"}>
              Save
            </Text>
          }
        </Pressable>
      ),
    });
  }, [
    navigation,
    headerLeft,
    handleSave,
    routineData,
    updateRoutineMutation.isPending,
    theme.colors.background,
    theme.colors.text,
  ]);

  return (
    <View flex={1}>
      {isWeb && (
        <View px="5xl" pt="4xl" pb="xl" bg="background" alignItems="flex-end">
          <Pressable onPress={handleSave} disabled={updateRoutineMutation.isPending || !routineData}>
            {updateRoutineMutation.isPending ?
              <ActivityIndicator size="small" />
            : <Text textStyle="md/semibold" color={routineData ? "navy" : "textMuted"}>
                Save
              </Text>
            }
          </Pressable>
        </View>
      )}
      <RoutineCustomization
        onSave={setRoutineData}
        onEdit={handleEdit}
        initialRoutine={initialRoutine}
        showHeader={false}
      />
    </View>
  );
}
